import { cannectDefaultProduct, cannectDefaultProductNoTarg } from '@cannect/assets/img'
import { cn } from '@cannect/lib/utils'
import { SEARCH_DEBOUNCE_MS } from '@cannect/constants/products'
import { Form } from '@cannect/new-components/atoms'
import { getPublicProductsList } from '@cannect/services/resources/products/requests'
import type { TProductDetailItem } from '@cannect/services/resources/products/types'
import { sendSearchWebhook } from '@cannect/services/resources/webhook/requests'
import useDebounce from '@cannect/hooks/utils/useDebounce'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { LuSearch } from 'react-icons/lu'

type SearchAutocompleteProps = {
  isLogged?: boolean
  className?: string
  placeholder?: string
  inputClassName?: string
}

const SpinnerComponent = () => <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />

export const SearchAutocomplete = ({
  isLogged,
  className,
  placeholder = 'O que você precisa?',
  inputClassName
}: SearchAutocompleteProps) => {
  const { register, watch } = useForm<{ q: string }>({ defaultValues: { q: '' } })
  const q = watch('q')
  const debouncedQ = useDebounce(q, SEARCH_DEBOUNCE_MS)
  const { signedUser } = useSignedUser()
  const [isSearching, setIsSearching] = useState(false)
  const [show, setShow] = useState(false)
  const [suggestions, setSuggestions] = useState<TProductDetailItem[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingNext, setIsFetchingNext] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const searchCacheRef = useRef<Map<string, TProductDetailItem[]>>(new Map())

  const slugify = (s?: string) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '')

  const formatBRL = (value?: number | string | null) => {
    const n = typeof value === 'string' ? parseFloat(value) : Number(value || 0)
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const getImage = (p: TProductDetailItem): string => {
    const defaultImg =
      p?.supplier?.id === 18 || p?.class?.id === 6 ? cannectDefaultProduct : cannectDefaultProductNoTarg
    if (!isLogged) return defaultImg
    const img = p?.media?.find?.((m: any) => m?.format === 'IMG')
    return img?.path || defaultImg
  }

  const handleSearchWebhook = useCallback(
    async (searchTerm: string, products?: TProductDetailItem[]) => {
      try {
        // Pegar os 4 primeiros produtos
        const firstFourProducts = products
          ? products.slice(0, 4).map((p) => ({
              id: p.id,
              name: p.translated_name || p.name,
              brand: p.brand?.name,
              price: p.real_price ?? p.price,
              promotional_price: p.promotion?.promotional_price,
              supplier: p.supplier?.name
            }))
          : []

        const webhookData = {
          search_term: searchTerm,
          user: {
            id: signedUser.id,
            uuid: signedUser.uuid,
            name: signedUser.name,
            email: signedUser.email,
            phone: signedUser.phone,
            cellphone: signedUser.cellphone,
            registration_type: signedUser.registration_type,
            is_logged: Boolean(isLogged),
            prescriber: signedUser.prescriber
              ? {
                  id: signedUser.prescriber.id,
                  council: signedUser.prescriber.council,
                  nr_council: signedUser.prescriber.nr_council,
                  uf_council: signedUser.prescriber.uf_council,
                  listed: signedUser.prescriber.listed
                }
              : null
          },
          search_results: {
            total_found: products?.length || 0,
            first_four_products: firstFourProducts
          },
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          url: window.location.href,
          referrer: document.referrer
        }

        // Enviar via backend para evitar CORS
        await sendSearchWebhook(webhookData)
      } catch (error) {
        console.warn('Webhook de busca falhou:', error)
      }
    },
    [signedUser, isLogged]
  )

  const searchProducts = useCallback(async (query: string, pageNum: number = 1) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    try {
      const params = new URLSearchParams()
      params.set('limit', '16')
      params.set('translated_name', query.trim())
      params.set('page', String(pageNum))

      const data = await getPublicProductsList(String(pageNum), params)
      const rows: TProductDetailItem[] = (data as any)?.products?.rows || (data as any)?.products || []

      if (pageNum === 1) {
        searchCacheRef.current.set(query.toLowerCase(), rows)
      }

      return { rows, hasMore: rows.length >= 16 }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null // Requisição foi cancelada
      }
      throw error
    }
  }, [])

  useEffect(() => {
    if (!debouncedQ || debouncedQ.trim().length < 4) {
      setSuggestions([])
      setPage(1)
      setHasMore(true)
      setIsSearching(false)
      return
    }

    const trimmedQuery = debouncedQ.trim()
    const cacheKey = trimmedQuery.toLowerCase()

    const cachedResults = searchCacheRef.current.get(cacheKey)
    if (cachedResults && cachedResults.length > 0) {
      setSuggestions(cachedResults)
      setPage(2)
      setHasMore(cachedResults.length >= 16)
      setIsSearching(false)
      return
    }

    setIsSearching(true)

    const performSearch = async () => {
      try {
        const result = await searchProducts(trimmedQuery, 1)
        if (result) {
          setSuggestions(result.rows)
          setPage(2)
          setHasMore(result.hasMore)

          await handleSearchWebhook(trimmedQuery, result.rows)
        }
      } catch {
        setSuggestions([])
        setHasMore(false)
      } finally {
        setIsSearching(false)
      }
    }

    performSearch()
  }, [debouncedQ, searchProducts, handleSearchWebhook])

  const fetchNextPage = async () => {
    if (!hasMore || isFetchingNext || !debouncedQ || debouncedQ.trim().length < 4) return
    try {
      setIsFetchingNext(true)
      const result = await searchProducts(debouncedQ.trim(), page)
      if (result && result.rows.length > 0) {
        // dedupe by id
        const map = new Map<number, TProductDetailItem>()
        ;[...suggestions, ...result.rows].forEach((item) => map.set(item.id, item))
        setSuggestions(Array.from(map.values()))
        setPage((p) => p + 1)
        setHasMore(result.hasMore)
      } else {
        setHasMore(false)
      }
    } catch {
      setHasMore(false)
    } finally {
      setIsFetchingNext(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShow(false)
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShow(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  const submit = () => {
    const query = (q || '').trim()
    if (!query) return
    const url = `/produtos/1?translated_name=${encodeURIComponent(query)}`
    window.location.href = url
  }

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <Form.Input
        size="sm"
        placeholder={placeholder}
        variant="search"
        className={cn('w-full', inputClassName)}
        rigthElement={<LuSearch />}
        {...register('q')}
        onFocus={() => setShow(true)}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          }
        }}
      />

      {show && !!q && q.trim().length >= 4 && (
        <div className="absolute left-0 top-[110%] z-50 w-full min-w-full max-w-[95vw] overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg lg:min-w-[720px]">
          {isSearching && (
            <div className="space-y-2 p-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md p-2 lg:gap-3">
                  <div className="h-8 w-8 animate-pulse rounded bg-neutral-200 lg:h-10 lg:w-10" />
                  <div className="flex-1 space-y-1">
                    <div className="h-2.5 w-3/4 animate-pulse rounded bg-neutral-200 lg:h-3" />
                    <div className="h-2.5 w-1/3 animate-pulse rounded bg-neutral-200 lg:h-3" />
                  </div>
                  <div className="h-3 w-12 animate-pulse rounded bg-neutral-200 lg:h-4 lg:w-16" />
                </div>
              ))}
            </div>
          )}
          {!isSearching && suggestions.length === 0 && (
            <div className="px-3 py-3 text-xs text-neutral-500 lg:px-4 lg:text-sm">Nenhum resultado</div>
          )}
          {!isSearching && suggestions.length > 0 && (
            <ul
              className="max-h-[32rem] w-full overflow-auto bg-white py-2"
              onScroll={(e) => {
                const el = e.currentTarget
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
                  fetchNextPage()
                }
              }}>
              {suggestions.map((p) => {
                const brand = p?.brand?.name || ''
                const name = p?.translated_name || p?.name || ''
                const path = `/detalhes-produtos/${slugify(brand)}/${slugify(name)}/${p.id}`
                const hasPromo = !!p?.promotion?.promotional_price
                const finalPrice = hasPromo
                  ? Number(p?.promotion?.promotional_price)
                  : Number(p?.real_price ?? p?.price ?? 0)
                const regularPrice = Number(p?.promotion?.regular_price ?? p?.price ?? 0)
                const discountPct =
                  hasPromo && regularPrice > 0 ? Math.round(((regularPrice - finalPrice) / regularPrice) * 100) : null
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => window.open(path, '_blank', 'noopener,noreferrer')}
                      className="flex h-21 w-full items-center gap-2 border-0 bg-white px-2 py-2 text-left hover:bg-neutral-50 lg:gap-3 lg:px-3">
                      <img
                        src={getImage(p)}
                        alt={name}
                        className="h-8 w-8 flex-shrink-0 rounded border border-neutral-200 object-cover lg:h-10 lg:w-10"
                      />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="line-clamp-1 text-xs font-medium text-neutral-800 lg:text-sm">{name}</span>
                        {brand && <span className="text-[10px] text-neutral-500 lg:text-xs">{brand}</span>}
                      </div>
                      {isLogged && (
                        <div className="flex flex-col items-end">
                          <span className="text-xs font-semibold text-primary-800 lg:text-sm">
                            R$ {formatBRL(finalPrice)}
                          </span>
                          {hasPromo && regularPrice > 0 && (
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-neutral-400 line-through lg:text-[11px]">
                                R$ {formatBRL(regularPrice)}
                              </span>
                              {discountPct !== null && (
                                <span className="success-600 text-[9px] italic lg:text-[11px]">-{discountPct}%</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  </li>
                )
              })}
              {isFetchingNext && (
                <li className="flex w-full items-center justify-center bg-white py-3">
                  <SpinnerComponent />
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchAutocomplete
